import * as React from 'react';

import type {Row} from 'src/types/byod';

/** Snapshot captured when a row leaves `rowOrder` so the canvas can render
 *  a same-height placeholder for the duration of the exit animation. */
export type ExitingRow = {
  rowId: string;
  rowType: string;
  /** Index in the *previous* rowOrder. Used to position the placeholder at
   *  the gap the row left behind. Indices >= the new rowOrder.length render
   *  as trailing placeholders below the canvas. */
  insertAt: number;
};

/** Default exit duration. Matches the CSS `canvasRowExiting` keyframe. */
const EXIT_DURATION_MS = 280;

/**
 * Tracks rows that have just been removed from `rowOrder` so the canvas can
 * keep rendering a height-matched placeholder for `EXIT_DURATION_MS`. Without
 * this the row collapses instantly on delete and the surrounding tiles snap.
 *
 * Implementation:
 *   1. Diff the previous `rowOrder` against the current one on every change.
 *   2. For each row that disappeared, push an entry into `exitingRows` with
 *      its old index + type so the canvas can render the right size at the
 *      right place.
 *   3. Clear that entry after EXIT_DURATION_MS via setTimeout.
 *
 * Pending timers are tracked in a ref so they can all be cleared on unmount —
 * otherwise a row deleted then the canvas unmounted (route change) would
 * leave a setState scheduled against a dead component.
 */
export function useExitAnimation(
  rowOrder: string[],
  rows: Record<string, Row>,
): ExitingRow[] {
  const [exitingRows, setExitingRows] = React.useState<ExitingRow[]>([]);
  const prevCanvasStateRef = React.useRef({rowOrder, rows});
  const exitTimersRef = React.useRef<Set<ReturnType<typeof setTimeout>>>(
    new Set(),
  );

  React.useEffect(() => {
    const prev = prevCanvasStateRef.current;
    if (prev.rowOrder === rowOrder) {
      prevCanvasStateRef.current = {rowOrder, rows};
      return undefined;
    }
    const currentSet = new Set(rowOrder);
    const removed: ExitingRow[] = [];
    prev.rowOrder.forEach((id, idx) => {
      const row = prev.rows[id];
      if (!currentSet.has(id) && row) {
        removed.push({rowId: id, rowType: row.type, insertAt: idx});
      }
    });
    prevCanvasStateRef.current = {rowOrder, rows};
    if (removed.length === 0) {
      return undefined;
    }
    setExitingRows((curr) => [...curr, ...removed]);
    const timers = exitTimersRef.current;
    const timer = setTimeout(() => {
      timers.delete(timer);
      setExitingRows((curr) =>
        curr.filter((e) => !removed.some((r) => r.rowId === e.rowId)),
      );
    }, EXIT_DURATION_MS);
    timers.add(timer);
    return undefined;
  }, [rowOrder, rows]);

  // Clear any pending exit-animation timers on unmount.
  React.useEffect(() => {
    const timers = exitTimersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  return exitingRows;
}
